import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  MapPin,
  Calendar,
  Table as TableIcon,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  delete_area_res,
  get_all_areas_res,
  server_post_data,
  toggle_show_hide_area_res,
} from "../ServiceConnection/serviceconnection";

export default function AreaManagement() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [areas, setAreas] = useState([]);
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch areas
  const fetchAreas = useCallback(async () => {
    setShowLoaderAdmin(true);
    setLoading(true);
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_all_areas_res, fd);

      if (!response.data.error) {
        const areaData = response.data.message.data || [];
        setAreas(areaData);
        setFilteredAreas(areaData);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching areas:", error);
      toast({
        title: "Error",
        description: "Failed to load areas",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setLoading(false);
    }
  }, [toast]);

  // Handle search
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);

      if (!query.trim()) {
        setFilteredAreas(areas);
        return;
      }

      const searchLower = query.toLowerCase();
      const results = areas.filter((area) => {
        return (
          area.dining_area_name?.toLowerCase().includes(searchLower) ||
          area.no_of_table?.toString().toLowerCase().includes(searchLower)
        );
      });

      setFilteredAreas(results);
    },
    [areas],
  );

  // Handle toggle status
  const handleToggleClick = useCallback((area) => {
    setSelectedArea(area);
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedArea) return;

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("primary_id", selectedArea.primary_id);

    try {
      const response = await server_post_data(toggle_show_hide_area_res, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Area status updated successfully",
        });
        fetchAreas();
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast({
        title: "Error",
        description: "Failed to update area status",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedArea(null);
    }
  }, [selectedArea, toast, fetchAreas]);

  // Handle delete
  const handleDeleteClick = useCallback((area) => {
    setSelectedArea(area);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedArea) return;

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("primary_id", selectedArea.primary_id);

    try {
      const response = await server_post_data(delete_area_res, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Area deleted successfully",
        });
        fetchAreas();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Failed to delete area",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setSelectedArea(null);
    }
  }, [selectedArea, toast, fetchAreas]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Initial load
  useEffect(() => {
    fetchAreas();
  }, [fetchAreas]);

  // Calculate stats
  const stats = {
    total: areas.length,
    active: areas.filter((a) => a.show_hide === 1 || a.show_hide === true)
      .length,
    totalTables: areas.reduce(
      (sum, area) => sum + (parseInt(area.no_of_table) || 0),
      0,
    ),
  };

  return (
    <MainLayout>
      <Header
        title="Area Management"
        subtitle="Manage dining areas and zones"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.total}
                </p>
                <p className="text-sm text-muted-foreground">Total Areas</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.active}
                </p>
                <p className="text-sm text-muted-foreground">Active Areas</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <TableIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalTables}
                </p>
                <p className="text-sm text-muted-foreground">Total Tables</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by area name or table count..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setFilteredAreas(areas);
                }}
              >
                Clear
              </Button>
            )}
          </div>
          <Button
            onClick={() => navigate("/add-area")}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add New Area
          </Button>
        </div>

        {/* Areas List */}
        <Card>
          <CardContent className="p-0">
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground w-[80px]">
                      Sr.No
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Entry Date
                      </div>
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Area Name
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <TableIcon className="w-4 h-4" />
                        Tables Count
                      </div>
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Status
                    </TableHead>
                    <TableHead className="text-muted-foreground text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-12 text-muted-foreground"
                      >
                        Loading areas...
                      </TableCell>
                    </TableRow>
                  ) : filteredAreas.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-muted-foreground"
                      >
                        No areas found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAreas.map((area, index) => (
                      <TableRow key={area.primary_id} className="border-border">
                        <TableCell className="font-medium">
                          {index + 1}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(area.entry_date)}
                        </TableCell>
                        <TableCell className="font-semibold">
                          {area.dining_area_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            <TableIcon className="w-3 h-3" />
                            {area.no_of_table} tables
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={
                              area.show_hide === 1 || area.show_hide === true
                            }
                            onCheckedChange={() => handleToggleClick(area)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate("/edit-area", {
                                  state: { primary_id: area.primary_id },
                                })
                              }
                              className="hover:bg-primary/10 hover:text-primary"
                              title="Edit Area"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(area)}
                              className="hover:bg-destructive/10 hover:text-destructive"
                              title="Delete Area"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Area</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete area{" "}
                <strong>{selectedArea?.dining_area_name}</strong>? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-destructive hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Toggle Status Confirmation Dialog */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Area Status</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to{" "}
                {selectedArea?.show_hide === 1 ||
                selectedArea?.show_hide === true
                  ? "deactivate"
                  : "activate"}{" "}
                area <strong>{selectedArea?.dining_area_name}</strong>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmToggle}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
